import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import campaignService, { Campaign } from '../../services/campaignService';
import { authService } from '../../services/authService';

interface ScheduleModalProps {
  campaign: Campaign;
  isOpen: boolean;
  onClose: () => void;
  onScheduled: () => void;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ 
  campaign, 
  isOpen, 
  onClose, 
  onScheduled 
}) => {
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campaignSegments, setCampaignSegments] = useState<string[]>([]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const loadCampaignDetails = useCallback(async () => {
    if (!campaign) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}/segments`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const segments = await response.json();
        setCampaignSegments(segments.map((s: any) => s.name));
      }
    } catch (error) {
      console.error('Error loading campaign segments:', error);
    }
  }, [API_BASE_URL, campaign]);

  useEffect(() => {
    if (isOpen && campaign) {
      loadCampaignDetails();
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
      setScheduleTime('09:00');
    }
  }, [isOpen, campaign, loadCampaignDetails]);

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) {
      setError('Please select both date and time');
      return;
    }

    const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`);
    
    // Check if date is in the past
    if (scheduledFor < new Date()) {
      setError('Schedule time must be in the future');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await campaignService.scheduleCampaign(campaign.id, scheduledFor.toISOString());
      
      onScheduled();
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to schedule campaign');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <CalendarIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <h3 className="text-lg font-medium text-gray-900">
              Schedule Campaign
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Campaign Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Campaign Details</h4>
              <p className="text-sm text-gray-600">{campaign.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {campaign.estimated_recipients} recipients
                {campaignSegments.length > 0 && (
                  <span> • {campaignSegments.join(', ')}</span>
                )}
              </p>
            </div>

            {/* Date Selection */}
            <div>
              <label htmlFor="schedule-date" className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Date
              </label>
              <input
                id="schedule-date"
                type="date"
                value={scheduleDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            {/* Time Selection */}
            <div>
              <label htmlFor="schedule-time" className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Time
              </label>
              <input
                id="schedule-time"
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            {/* Timezone Display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <div className="bg-gray-50 rounded-md px-3 py-2">
                <p className="text-sm text-gray-600">{timezone}</p>
              </div>
            </div>

            {/* Preview */}
            {scheduleDate && scheduleTime && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">
                      Campaign will be sent on:
                    </p>
                    <p>
                      {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end p-6 bg-gray-50 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={loading || !scheduleDate || !scheduleTime}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Scheduling...
              </>
            ) : (
              <>
                <CalendarIcon className="h-4 w-4 mr-2" />
                Schedule Campaign
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;